var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import { alphalendWebSocketService } from '../services/alphalendWebsocket';
import { useAccountHistorySync } from './useAccountHistorySync';
import { transformBackendTransaction } from '../utils/transactionUtils';
// Action types (defined locally to avoid dependency on old websocket types)
const AccountHistoryActions = {
    AccountHistory: 'account_history',
    ExportAccountHistory: 'export_account_history',
};
function isAccountHistoryPayload(data) {
    if (!data || typeof data !== 'object') {
        return false;
    }
    const payload = data;
    const hasPositionCapId = typeof payload.position_cap_id === 'string';
    const hasTransactions = Array.isArray(payload.transactions);
    const hasValidCursor = payload.next_cursor === undefined ||
        payload.next_cursor === null ||
        typeof payload.next_cursor === 'string';
    const hasValidTotalTransactions = payload.total_transactions === undefined || typeof payload.total_transactions === 'number';
    const hasValidTotalEvents = payload.total_events === undefined || typeof payload.total_events === 'number';
    return (hasPositionCapId &&
        hasTransactions &&
        hasValidCursor &&
        hasValidTotalTransactions &&
        hasValidTotalEvents);
}
export function useAccountHistory(positionCapId, initialPageSize = 5, loadMorePageSize = 5) {
    const syncProgress = useAccountHistorySync(positionCapId);
    const [transactions, setTransactions] = useState([]);
    const [hasMoreData, setHasMoreData] = useState(false);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [nextCursor, setNextCursor] = useState(null);
    const [totalTransactions, setTotalTransactions] = useState(0);
    const [totalEvents, setTotalEvents] = useState(0);
    const requestTypeRef = useRef('initial');
    const lastProcessedCursorRef = useRef(null);
    const isExportingRef = useRef(false);
    // Message key for account history data
    const messageKey = useMemo(() => {
        return positionCapId ? `account_history:${positionCapId}` : undefined;
    }, [positionCapId]);
    // Subscribe to account history sync notifications
    useEffect(() => {
        if (!positionCapId) {
            return;
        }
        alphalendWebSocketService.subscribeToAccountHistory(positionCapId);
        return () => {
            alphalendWebSocketService.unsubscribeFromAccountHistory(positionCapId);
        };
    }, [positionCapId]);
    // Memoized refresh function
    const refresh = useCallback(() => {
        if (!positionCapId) {
            return;
        }
        setLoading(true);
        setLoadingMore(false);
        requestTypeRef.current = 'refresh';
        lastProcessedCursorRef.current = null;
        alphalendWebSocketService.sendMessage({
            action: AccountHistoryActions.AccountHistory,
            position_cap_id: positionCapId,
            data: { params: { limit: initialPageSize } },
        });
    }, [positionCapId, initialPageSize]);
    // Auto-refresh when sync completes successfully
    useEffect(() => {
        if (syncProgress.shouldRefresh) {
            refresh();
        }
    }, [syncProgress.shouldRefresh, refresh]);
    // Reset state when position changes and fetch initial data
    useEffect(() => {
        if (!positionCapId) {
            setTransactions([]);
            setLoading(false);
            setHasMoreData(false);
            setTotalTransactions(0);
            setTotalEvents(0);
            setNextCursor(null);
            requestTypeRef.current = 'initial';
            lastProcessedCursorRef.current = null;
            return;
        }
        setTransactions([]);
        setLoading(true);
        setLoadingMore(false);
        setHasMoreData(false);
        setTotalTransactions(0);
        setTotalEvents(0);
        setNextCursor(null);
        requestTypeRef.current = 'initial';
        lastProcessedCursorRef.current = null;
        alphalendWebSocketService.sendMessage({
            action: AccountHistoryActions.AccountHistory,
            position_cap_id: positionCapId,
            data: { params: { limit: initialPageSize } },
        });
    }, [positionCapId, initialPageSize]);
    // Handle incoming account history data messages
    useEffect(() => {
        if (!messageKey || !positionCapId) {
            return;
        }
        const handleMessage = (message) => {
            var _a;
            if (!(message === null || message === void 0 ? void 0 : message.data)) {
                return;
            }
            if (!isAccountHistoryPayload(message.data)) {
                console.error('Invalid account history payload structure:', message.data);
                return;
            }
            const payload = message.data;
            if (payload.position_cap_id !== positionCapId) {
                return;
            }
            if (isExportingRef.current) {
                return;
            }
            // Transform backend transactions to frontend format
            const newTransactions = (payload.transactions || []).map((tx) => {
                return transformBackendTransaction(tx);
            });
            const currentRequestType = requestTypeRef.current;
            const messageCursor = payload.next_cursor || 'no-cursor';
            const lastCursor = lastProcessedCursorRef.current;
            // Skip if we've already processed this exact cursor for the current request type
            if (currentRequestType === 'loadMore' && messageCursor === lastCursor) {
                return;
            }
            // Handle different request types
            setTransactions((prev) => {
                if (currentRequestType === 'loadMore') {
                    const existingDigests = new Set(prev.map((tx) => tx.digest));
                    const uniqueNew = newTransactions.filter((tx) => !existingDigests.has(tx.digest));
                    return [...prev, ...uniqueNew];
                }
                else {
                    return newTransactions.sort((a, b) => b.timestamp - a.timestamp);
                }
            });
            // Update pagination state
            setNextCursor((_a = payload.next_cursor) !== null && _a !== void 0 ? _a : null);
            setHasMoreData(!!payload.next_cursor);
            setTotalTransactions(payload.total_transactions || 0);
            setTotalEvents(payload.total_events || 0);
            lastProcessedCursorRef.current = messageCursor;
            setLoading(false);
            setLoadingMore(false);
            if (currentRequestType !== 'loadMore') {
                requestTypeRef.current = 'initial';
            }
        };
        // Subscribe using the new addMessageListener method
        const unsubscribe = alphalendWebSocketService.addMessageListener(messageKey, handleMessage);
        return () => {
            unsubscribe();
        };
    }, [messageKey, positionCapId]);
    const loadMore = useCallback(() => {
        if (!hasMoreData || !nextCursor || loadingMore || !positionCapId) {
            return;
        }
        setLoadingMore(true);
        requestTypeRef.current = 'loadMore';
        lastProcessedCursorRef.current = null;
        alphalendWebSocketService.sendMessage({
            action: AccountHistoryActions.AccountHistory,
            position_cap_id: positionCapId,
            data: { params: { limit: loadMorePageSize, cursor: nextCursor } },
        });
    }, [hasMoreData, nextCursor, loadingMore, positionCapId, loadMorePageSize]);
    const fetchAllTransactions = useCallback(() => __awaiter(this, void 0, void 0, function* () {
        if (!positionCapId)
            return [];
        isExportingRef.current = true;
        const allTransactions = [];
        try {
            const exportPromise = new Promise((resolve, reject) => {
                const timeout = setTimeout(() => {
                    cleanup();
                    reject(new Error('Export timeout after 30 seconds'));
                }, 30000);
                let removeChunkListener = null;
                let removeCompleteListener = null;
                // Listen for chunk messages
                removeChunkListener = alphalendWebSocketService.addMessageListener(`export_chunk:${positionCapId}`, (message) => {
                    if (!message.data)
                        return;
                    const chunkData = message.data;
                    // Transform backend transactions to frontend format
                    const newTransactions = chunkData.transactions.map((tx) => transformBackendTransaction(tx));
                    allTransactions.push(...newTransactions);
                });
                // Listen for completion message
                removeCompleteListener = alphalendWebSocketService.addMessageListener(`export_complete:${positionCapId}`, () => {
                    cleanup();
                    resolve();
                });
                const cleanup = () => {
                    clearTimeout(timeout);
                    removeChunkListener === null || removeChunkListener === void 0 ? void 0 : removeChunkListener();
                    removeCompleteListener === null || removeCompleteListener === void 0 ? void 0 : removeCompleteListener();
                };
            });
            // Send the export request
            alphalendWebSocketService.sendMessage({
                action: AccountHistoryActions.ExportAccountHistory,
                position_cap_id: positionCapId,
                data: {},
            });
            // Wait for completion
            yield exportPromise;
            yield new Promise((resolve) => setTimeout(resolve, 2000));
            return allTransactions.sort((a, b) => a.timestamp - b.timestamp);
        }
        catch (error) {
            console.error('CSV Export failed:', error);
            throw error;
        }
        finally {
            isExportingRef.current = false;
        }
    }), [positionCapId]);
    return useMemo(() => ({
        transactions,
        loading,
        loadingMore,
        hasMoreData,
        totalTransactions,
        totalEvents,
        loadMore,
        refresh,
        syncProgress,
        fetchAllTransactions,
    }), [
        transactions,
        loading,
        loadingMore,
        hasMoreData,
        totalTransactions,
        totalEvents,
        loadMore,
        refresh,
        syncProgress,
        fetchAllTransactions,
    ]);
}
